import { StorageService, STORAGE_KEYS } from './storage';
import { getSupabaseClient, getStoredSupabaseConfig } from './supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error' | 'offline';

export interface CloudSyncState {
  status: SyncStatus;
  lastSyncedAt: string | null;
  error: string | null;
  syncKey: string;
  autoSync: boolean;
  endpoint: string;
  provider: 'supabase' | 'api' | 'local';
}

const STORAGE_SYNC_KEY = 'mylife_sync_user_key';
const STORAGE_SYNC_ENDPOINT = 'mylife_sync_endpoint';
const STORAGE_LAST_SYNCED = 'mylife_last_synced_at';
const STORAGE_AUTO_SYNC = 'mylife_auto_sync_enabled';
const STORAGE_LAST_LOCAL_EDIT = 'mylife_last_local_edit_timestamp';

type RemoteUpdateCallback = (data: Record<string, any>) => void;

class CloudSyncEngine {
  private status: SyncStatus = 'idle';
  private lastSyncedAt: string | null = localStorage.getItem(STORAGE_LAST_SYNCED);
  private error: string | null = null;
  private syncKey: string = localStorage.getItem(STORAGE_SYNC_KEY) || 'user_tahsin';
  private endpoint: string = localStorage.getItem(STORAGE_SYNC_ENDPOINT) || '/api';
  private autoSync: boolean = localStorage.getItem(STORAGE_AUTO_SYNC) !== 'false';
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners: Set<(state: CloudSyncState) => void> = new Set();
  private onRemoteUpdateCallback: RemoteUpdateCallback | null = null;
  private isPulling = false;
  private isPushing = false;
  private realtimeChannel: RealtimeChannel | null = null;
  private clientId: string = 'cli_' + Math.random().toString(36).substring(2, 9) + Date.now();
  private lastLocalEditTime: number = Number(localStorage.getItem(STORAGE_LAST_LOCAL_EDIT) || 0);

  constructor() {
    if (typeof window !== 'undefined') {
      // Auto push whenever localStorage changes in any component
      StorageService.onStorageChange((key) => {
        if (key.startsWith('mylife_') && !key.startsWith('mylife_last_') && key !== STORAGE_AUTO_SYNC) {
          this.triggerAutoPush();
        }
      });

      window.addEventListener('online', () => {
        this.setStatus('idle');
        this.pullFromCloud();
      });

      window.addEventListener('offline', () => {
        this.setStatus('offline');
      });

      window.addEventListener('focus', () => {
        if (this.autoSync && navigator.onLine && !this.isPushing && !this.pushTimer) {
          this.pullFromCloud();
        }
      });

      // Periodic background heartbeat sync (every 30 seconds)
      setInterval(() => {
        if (
          this.autoSync &&
          navigator.onLine &&
          !this.isPulling &&
          !this.isPushing &&
          !this.pushTimer &&
          Date.now() - this.lastLocalEditTime > 3000
        ) {
          this.pullFromCloud();
        }
      }, 30000);
    }
  }

  public getState(): CloudSyncState {
    const supabaseConfig = getStoredSupabaseConfig();
    const provider: 'supabase' | 'api' | 'local' = supabaseConfig.isConfigured
      ? 'supabase'
      : this.endpoint
      ? 'api'
      : 'local';

    return {
      status: this.status,
      lastSyncedAt: this.lastSyncedAt,
      error: this.error,
      syncKey: this.syncKey,
      autoSync: this.autoSync,
      endpoint: this.endpoint,
      provider,
    };
  }

  public subscribe(listener: (state: CloudSyncState) => void): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  private notify() {
    const state = this.getState();
    this.listeners.forEach((l) => l(state));
  }

  private setStatus(status: SyncStatus, error: string | null = null) {
    this.status = status;
    this.error = error;
    this.notify();
  }

  public setSyncKey(key: string) {
    const clean = key.trim() || 'user_tahsin';
    this.syncKey = clean;
    localStorage.setItem(STORAGE_SYNC_KEY, clean);
    this.notify();
    this.setupSupabaseRealtime();
    this.pullFromCloud(true);
  }

  public setEndpoint(endpoint: string) {
    const clean = endpoint.trim() || '/api';
    this.endpoint = clean;
    localStorage.setItem(STORAGE_SYNC_ENDPOINT, clean);
    this.notify();
  }

  public setAutoSync(enabled: boolean) {
    this.autoSync = enabled;
    localStorage.setItem(STORAGE_AUTO_SYNC, enabled ? 'true' : 'false');
    this.notify();
  }

  public init(callback: RemoteUpdateCallback) {
    this.onRemoteUpdateCallback = callback;
    this.setupSupabaseRealtime();

    if (navigator.onLine) {
      this.pullFromCloud();
    } else {
      this.setStatus('offline');
    }
  }

  /**
   * Set up Supabase Realtime WebSocket listener for instant zero-latency cross-device sync
   */
  public setupSupabaseRealtime() {
    const supabase = getSupabaseClient();
    if (!supabase) {
      if (this.realtimeChannel) {
        this.realtimeChannel.unsubscribe();
        this.realtimeChannel = null;
      }
      return;
    }

    if (this.realtimeChannel) {
      this.realtimeChannel.unsubscribe();
    }

    try {
      this.realtimeChannel = supabase
        .channel(`mylife_realtime_${this.syncKey}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'mylife_sync',
            filter: `id=eq.${this.syncKey}`,
          },
          (payload: any) => {
            const newRecord = payload.new;
            if (!newRecord || !newRecord.data) return;

            // 1. Ignore if this is our own client's push echo
            if (newRecord.data._client_id === this.clientId) {
              return;
            }

            // 2. Ignore if we have active unpushed local changes in progress
            if (this.pushTimer !== null || this.isPushing || Date.now() - this.lastLocalEditTime < 1500) {
              return;
            }

            this.applyRemoteData(newRecord.data);
            const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            this.lastSyncedAt = now;
            localStorage.setItem(STORAGE_LAST_SYNCED, now);
            this.setStatus('synced');
          }
        )
        .subscribe();
    } catch (err) {
      console.warn('Could not initialize Supabase Realtime channel:', err);
    }
  }

  private applyRemoteData(remoteData: Record<string, any>) {
    if (!remoteData || typeof remoteData !== 'object') return;

    const mergedData: Record<string, any> = {};

    const syncCollection = (key: string, storageKey: string) => {
      if (remoteData[key] !== undefined) {
        mergedData[key] = remoteData[key];
        StorageService.setItem(storageKey, remoteData[key], true); // silent = true
      }
    };

    syncCollection('accounts', STORAGE_KEYS.ACCOUNTS);
    syncCollection('categories', STORAGE_KEYS.CATEGORIES);
    syncCollection('transactions', STORAGE_KEYS.TRANSACTIONS);
    syncCollection('budgets', STORAGE_KEYS.BUDGETS);
    syncCollection('debts', STORAGE_KEYS.DEBTS);
    syncCollection('savingsGoals', STORAGE_KEYS.SAVINGS_GOALS);
    syncCollection('recurringBills', STORAGE_KEYS.RECURRING_BILLS);
    syncCollection('notes', STORAGE_KEYS.NOTES);
    syncCollection('tasks', STORAGE_KEYS.TASKS);
    syncCollection('habits', STORAGE_KEYS.HABITS);
    syncCollection('habitLogs', STORAGE_KEYS.HABIT_LOGS);
    syncCollection('personalReports', STORAGE_KEYS.PERSONAL_REPORTS);
    syncCollection('yearlyGoals', STORAGE_KEYS.YEARLY_GOALS);
    syncCollection('resources', STORAGE_KEYS.RESOURCES);
    syncCollection('financeFolders', STORAGE_KEYS.FINANCE_FOLDERS);
    syncCollection('monthlyPlans', STORAGE_KEYS.MONTHLY_PLANS);
    syncCollection('atAGlanceReports', STORAGE_KEYS.AT_A_GLANCE_REPORTS);

    // Extra ledger and custom categories
    if (remoteData.monthlyExpenses !== undefined) {
      StorageService.setItem('mylife_monthly_ledger_expenses', remoteData.monthlyExpenses, true);
    }
    if (remoteData.monthlyIncomes !== undefined) {
      StorageService.setItem('mylife_monthly_ledger_incomes', remoteData.monthlyIncomes, true);
    }
    if (remoteData.monthlyDisbursements !== undefined) {
      StorageService.setItem('mylife_monthly_ledger_disbursements', remoteData.monthlyDisbursements, true);
    }
    if (remoteData.calendarEvents !== undefined) {
      StorageService.setItem('mylife_calendar_custom_events', remoteData.calendarEvents, true);
    }
    if (remoteData.orgCategories !== undefined) {
      StorageService.setItem('mylife_org_categories', remoteData.orgCategories, true);
    }
    if (remoteData.personalCategories !== undefined) {
      StorageService.setItem('mylife_personal_categories', remoteData.personalCategories, true);
    }
    if (remoteData.businessCategories !== undefined) {
      StorageService.setItem('mylife_business_categories', remoteData.businessCategories, true);
    }
    if (remoteData.masterPin !== undefined) {
      StorageService.setItem('mylife_notes_master_pin', remoteData.masterPin, true);
    }

    if (this.onRemoteUpdateCallback) {
      this.onRemoteUpdateCallback(mergedData);
    }
  }

  private collectLocalSnapshot() {
    const allData = StorageService.loadAll();
    const extraLedger = {
      monthlyExpenses: StorageService.getItem('mylife_monthly_ledger_expenses', []),
      monthlyIncomes: StorageService.getItem('mylife_monthly_ledger_incomes', []),
      monthlyDisbursements: StorageService.getItem('mylife_monthly_ledger_disbursements', []),
      calendarEvents: StorageService.getItem('mylife_calendar_custom_events', []),
      orgCategories: StorageService.getItem('mylife_org_categories', []),
      personalCategories: StorageService.getItem('mylife_personal_categories', []),
      businessCategories: StorageService.getItem('mylife_business_categories', []),
      masterPin: StorageService.getItem('mylife_notes_master_pin', null),
    };

    return {
      version: '1.0.0',
      syncKey: this.syncKey,
      client_id: this.clientId,
      pushedAt: new Date().toISOString(),
      data: {
        ...allData,
        ...extraLedger,
        _client_id: this.clientId,
        _pushed_at: new Date().toISOString(),
      },
    };
  }

  /**
   * Schedule automatic push to cloud on data change (Debounced 800ms)
   */
  public triggerAutoPush() {
    this.lastLocalEditTime = Date.now();
    localStorage.setItem(STORAGE_LAST_LOCAL_EDIT, this.lastLocalEditTime.toString());
    if (!this.autoSync || !navigator.onLine) return;

    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
    }

    this.pushTimer = setTimeout(() => {
      this.pushTimer = null;
      this.pushToCloud();
    }, 800);
  }

  /**
   * Push all current data to Supabase or Cloud API
   */
  public async pushToCloud(): Promise<boolean> {
    if (!navigator.onLine) {
      this.setStatus('offline');
      return false;
    }

    this.isPushing = true;
    this.setStatus('syncing');

    const payload = this.collectLocalSnapshot();

    // 1. Check Supabase first
    const supabase = getSupabaseClient();
    if (supabase) {
      try {
        const { error } = await supabase.from('mylife_sync').upsert({
          id: this.syncKey,
          data: payload.data,
          updated_at: new Date().toISOString(),
        });

        if (!error) {
          const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
          this.lastSyncedAt = now;
          localStorage.setItem(STORAGE_LAST_SYNCED, now);
          this.setStatus('synced');
          this.isPushing = false;
          return true;
        } else {
          console.warn('Supabase upsert error:', error.message);
        }
      } catch (err: any) {
        console.warn('Supabase push exception, trying API fallback:', err.message);
      }
    }

    // 2. Fallback to REST API endpoint
    try {
      const res = await fetch(`${this.endpoint.replace(/\/$/, '')}/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.syncKey}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}: ${res.statusText}`);
      }

      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      this.lastSyncedAt = now;
      localStorage.setItem(STORAGE_LAST_SYNCED, now);
      this.setStatus('synced');
      this.isPushing = false;
      return true;
    } catch (err: any) {
      this.setStatus('error', err.message || 'Sync connection error');
      this.isPushing = false;
      return false;
    }
  }

  /**
   * Pull latest data from Supabase or Cloud API
   */
  public async pullFromCloud(force = false): Promise<boolean> {
    if (this.isPulling || !navigator.onLine) {
      if (!navigator.onLine) this.setStatus('offline');
      return false;
    }

    // Don't pull if we have pending local unpushed changes unless forced
    if (!force && (this.pushTimer !== null || this.isPushing || Date.now() - this.lastLocalEditTime < 1500)) {
      return false;
    }

    this.isPulling = true;
    this.setStatus('syncing');

    // 1. Check Supabase first
    const supabase = getSupabaseClient();
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('mylife_sync')
          .select('data, updated_at')
          .eq('id', this.syncKey)
          .single();

        if (!error && data && data.data) {
          const remoteTime = new Date(data.updated_at || 0).getTime();
          const localTime = Number(localStorage.getItem(STORAGE_LAST_LOCAL_EDIT) || 0);

          // If local has strictly newer unpushed changes, push to cloud instead of overriding local
          if (!force && localTime > remoteTime + 2000) {
            this.isPulling = false;
            return this.pushToCloud();
          }

          // Otherwise apply remote data if not our own echo
          if (data.data._client_id !== this.clientId || force) {
            this.applyRemoteData(data.data);
          }

          const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
          this.lastSyncedAt = now;
          localStorage.setItem(STORAGE_LAST_SYNCED, now);
          this.setStatus('synced');
          this.isPulling = false;
          return true;
        } else if (error && error.code === 'PGRST116') {
          // Record doesn't exist yet on remote, initialize it with local data
          this.isPulling = false;
          return this.pushToCloud();
        }
      } catch (err) {
        console.warn('Supabase pull failed, falling back to API:', err);
      }
    }

    // 2. Fallback to REST API endpoint
    try {
      const res = await fetch(`${this.endpoint.replace(/\/$/, '')}/sync`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.syncKey}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }

      const json = (await res.json()) as any;
      const remoteData = json.data?.data || json.data;

      if (remoteData && typeof remoteData === 'object') {
        this.applyRemoteData(remoteData);
      }

      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      this.lastSyncedAt = now;
      localStorage.setItem(STORAGE_LAST_SYNCED, now);
      this.setStatus('synced');
      this.isPulling = false;
      return true;
    } catch (err: any) {
      this.setStatus('error', err.message || 'Sync error');
      this.isPulling = false;
      return false;
    }
  }
}

export const CloudSync = new CloudSyncEngine();
